import Seo from "@/components/Seo";
import * as Icons from "lucide-react";
import { SERVICES } from "@/lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// Custom puffy cloud icon — more bubbles than the default lucide Cloud
const CloudPuffy = ({ size = 24, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M3.5 17a3 3 0 0 1 .8-5.8 4 4 0 0 1 3.4-3.6 4.5 4.5 0 0 1 8.5 1 3.8 3.8 0 0 1 4.6 4.2 3 3 0 0 1-2.8 4.2H3.5z" />
  </svg>
);

export default function Services() {
  const process = [
    { step: "01", title: "Consultation", body: "We scope your situation, site type and urgency to assign the right consultant." },
    { step: "02", title: "Site Assessment", body: "On-site inspection, sampling and documentation aligned to NSW and federal guidelines." },
    { step: "03", title: "Laboratory Analysis", body: "Samples analysed exclusively through NATA-accredited laboratories." },
    { step: "04", title: "Reporting", body: "Detailed reports with risk ratings, compliance notes and clear remediation pathways." },
    { step: "05", title: "Remediation Oversight", body: "Where required, we manage licensed removalists, air monitoring and clearance." },
    { step: "06", title: "Clearance Certification", body: "Clearance Inspections and Validation based sampling." },
  ];
  return (
    <div data-testid="services-page">
      <Seo title="Asbestos Services NSW" description="Site inspections, sampling, risk assessments, asbestos & hazmat management plans, air monitoring, clearance certificates and project management across NSW." path="/services" />

      {/* HERO — modern editorial with depth */}
      <section className="relative overflow-hidden border-b border-[#0E2A5C]/8 bg-gradient-to-br from-[#FAFCF3] via-[#F0F6E4] to-[#EAF1F8]">
        {/* Floating colour orbs (continue green→blue gradient feel) */}
        <div className="orb-a absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full bg-[#A6CD3C]/35 blur-3xl pointer-events-none" aria-hidden />
        <div className="orb-b absolute -bottom-32 right-[-80px] w-[460px] h-[460px] rounded-full bg-[#3B6CA8]/30 blur-3xl pointer-events-none" aria-hidden />

        {/* Full-width dot grid */}
        <div
          className="absolute inset-0 opacity-35 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #0E2A5C 1.5px, transparent 0)", backgroundSize: "32px 32px" }}
          aria-hidden
        />

        {/* Giant ghost ISGS lettering — centerpiece */}
        <div
          aria-hidden
          className="absolute inset-0 flex items-center justify-center select-none pointer-events-none"
        >
          <span
            style={{
              fontSize: "clamp(260px, 44vw, 680px)",
              fontWeight: 900,
              lineHeight: 0.85,
              color: "#0E2A5C",
              opacity: 0.15,
              letterSpacing: "-0.05em",
            }}
          >
            ISGS
          </span>
        </div>

        <div className="container-x relative z-10 py-28 lg:py-36 text-center">
          {/* Eyebrow with pulse */}
          <div className="inline-flex items-center gap-2 bg-white border border-[#0E2A5C]/15 rounded-full px-3.5 py-1 mb-6 shadow-sm rise">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C] badge-pulse" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E2A5C]">Services</span>
          </div>

          {/* Animated lime accent bar */}
          <div className="accent-sweep mx-auto w-20 h-[3px] bg-[#A6CD3C] mb-6 rounded-full" />

          <h1 className="rise rise-d1 text-[64px] md:text-[96px] lg:text-[120px] font-bold leading-[0.95] text-[#0E2A5C] tracking-[-0.03em]">
            <span className="relative inline-block">
              <span>Services</span>
              <span className="absolute inset-x-0 bottom-2 h-4 md:h-5 bg-[#A6CD3C]/55 -z-10" />
            </span>
            <span className="text-[#A6CD3C]">.</span>
          </h1>

          <p className="rise rise-d2 mt-8 text-[#0B0F1A]/70 text-[16.5px] md:text-[19px] max-w-2xl mx-auto leading-relaxed">
            Specialist Asbestos and Hazardous Material services covering the full asbestos lifecycle, subject matter advice prior from first inspection all the way to the final clearance certificate.
          </p>
        </div>
      </section>

      <section className="section-pad bg-[#A6CD3C]/30">
        <div className="container-x grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon === "Cloud" ? CloudPuffy : (Icons[s.icon] || Icons.ShieldCheck);
            const accents = [
              "bg-[#0E2A5C] text-[#A6CD3C]",
              "bg-[#A6CD3C] text-[#0E2A5C]",
              "bg-[#3B6CA8] text-white",
              "bg-[#D89A3D] text-white",
              "bg-[#0E2A5C] text-[#A6CD3C]",
              "bg-[#A6CD3C] text-[#0E2A5C]",
              "bg-[#3B6CA8] text-white",
              "bg-[#D89A3D] text-white",
            ];
            return (
              <div key={s.title} className="bg-white rounded-2xl p-8 border border-[#0E2A5C]/10 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(14,42,92,0.3)] hover:border-[#A6CD3C]" data-testid={`service-full-${i}`}>
                <div className="mb-6">
                  <div className={`w-14 h-14 rounded-xl ${accents[i % accents.length]} flex items-center justify-center`}>
                    <Icon size={24} />
                  </div>
                </div>
                <h3 className="text-[19px] font-semibold text-[#0E2A5C] mb-3">{s.title}</h3>
                <p className="text-[#5B6478] text-[14.5px] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section-pad bg-[#0E2A5C] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-25" />
        <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="text-[32px] md:text-[44px] font-bold leading-[1.05] mb-7">Why teams choose <span className="text-[#A6CD3C]">ISGS</span> over generalist consultants.</h2>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {["18+ years asbestos consulting experience", "Exclusively NATA-accredited laboratory partners", "Independent — zero conflict of interest", "Fast turnaround on emergency response jobs", "Reporting that holds up to audit", "Tailored Asbestos Advice and Solutions"].map((point) => (
                <li key={point} className="flex items-start gap-3 text-white/85 text-[14.5px]">
                  <CheckCircle2 className="text-[#A6CD3C] mt-1 shrink-0" size={18} />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/contact" className="btn-primary" data-testid="services-cta">
              Request a Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#F7F8F3]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <div className="eyebrow mb-4">Our process</div>
              <h2 className="text-[34px] md:text-[44px] text-[#0E2A5C] leading-tight">
                A repeatable, compliant <span className="italic font-light">workflow.</span>
              </h2>
              <p className="text-[#5B6478] mt-5 leading-relaxed">From first call to clearance certificate, every step is documented and reviewed.</p>
            </div>
            <div className="lg:col-span-8 space-y-3">
              {process.map((p) => (
                <div key={p.step} className="flex gap-6 p-7 bg-white rounded-2xl border border-[#0E2A5C]/8 hover:border-[#A6CD3C] transition-colors">
                  <div className="text-[36px] font-bold text-[#A6CD3C] leading-none w-16 shrink-0">{p.step}</div>
                  <div>
                    <h3 className="text-[19px] font-semibold text-[#0E2A5C] mb-1.5">{p.title}</h3>
                    <p className="text-[#5B6478] text-[14.5px] leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
